import { DefaultButton, Spinner } from '@fluentui/react';
import axios from 'axios';
import React, {useState, useEffect} from 'react';
import Plot from 'react-plotly.js';

const SITES = [
  {text: "West Frazer Pump Plant", key: "06175100"},
  {text: "East Frazer Pump Plant", key: "06175510"},
  {text: "Wolf Point", key: "06177000"},
  {text: "Culbertson", key: "06185500"},
  {text: "Williston", key: "06330000"}
];

const IMAGERY_DATES = [
  "2012-06-01",
  "2013-06-10",
  "2013-06-15",
  "2013-07-29",
  "2013-06-13",
  "2014-05-18",
  "2014-05-21",
  "2014-06-03",
  "2014-06-09",
  "2016-05-19",
  "2016-05-29",
  "2016-05-18",
  "2017-05-20",
  "2017-05-28",
  "2017-06-05",
  "2017-05-18",
  "2018-07-10",
  "2018-07-20",
  "2018-07-15",
  "2018-06-02",
  "2024-04-09",
  "2024-05-04",
  "2024-06-26"
];

const DATE_RANGES = [
  {name: "June 2012", range: ['2012-06-01', '2012-06-30']},
  {name: "June 2013", range: ['2013-06-01', '2013-06-30']},
  {name: "May 2014", range: ['2014-05-01', '2014-05-31']},
  {name: "May 2016", range: ['2016-05-01', '2016-05-31']},
  {name: "May 2017", range: ['2017-05-01', '2017-05-31']},
  // {name: "June - July 2018", range: ['2018-06-01', '2018-07-31']},
  {name: "April 2024", range: ['2024-04-01', '2024-04-30']},
  {name: "May 2024", range: ['2024-05-01', '2024-05-31']},
  {name: "June 2024", range: ['2024-06-01', '2024-06-30']}
];

export default function BankAnalysis(props){
  const [dataPoints, setDataPoints] = useState([]);
  const [timestamps, setTimestamps] = useState([]);
  const [site, setSite] = useState(SITES[2]);
  const [loading, setLoading] = useState(false);
  const [selectedRange, setSelectedRange] = useState(null);
  const [stats, setStats] = useState({ min: null, max: null, median: null });


  useEffect(() => {
    // console.log(site);
    const fetchData = async (site) => {
      setLoading(true);
      const url = `https://nwis.waterservices.usgs.gov/nwis/iv/?sites=${site.key}&agencyCd=USGS&startDT=1990-01-01T00:00:00.000-07:00&endDT=2024-12-31T23:59:59.999-07:00&parameterCd=00060&format=json`;

      try {
        const response = await axios.get(url);
        const values = response.data.value.timeSeries[0].values[0].value;

        const flowValues = values.map(entry => entry.value === '-999999' ? null : parseFloat(entry.value));
        const timeValues = values.map(entry => entry.dateTime);

        setDataPoints(flowValues);
        setTimestamps(timeValues);
        setLoading(false);
      } catch (error) {
        setDataPoints([]);
        setTimestamps([]);
        setLoading(false);
        console.error('Error fetching USGS data:', error);
      }
    };

    fetchData(site);
  }, [site]);

  useEffect(() => {
    // console.log(selectedRange);
    if(!selectedRange) return;
    const start = new Date(selectedRange.range[0]);
    const end = new Date(selectedRange.range[1]);
  
    const filtered = timestamps
      .map((ts, i) => ({ time: new Date(ts), value: dataPoints[i] }))
      .filter(d => d.time >= start && d.time <= end)
      .map(d => d.value)
      .filter(v => !isNaN(v));

    if (filtered.length > 0) {
      const sorted = [...filtered].sort((a, b) => a - b);
      const min = Math.min(...filtered);
      const max = Math.max(...filtered);
      const median = sorted.length % 2 === 0
        ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
        : sorted[Math.floor(sorted.length / 2)];
      setStats({ min, max, median: parseFloat(median) });
    } else {
      setStats({ min: null, max: null, median: null });
    }
  }, [selectedRange]);

  // const handleRelayout = (event) => {
  //   const startRaw = event['xaxis.range[0]'] || event['xaxis.range']?.[0];
  //   const endRaw = event['xaxis.range[1]'] || event['xaxis.range']?.[1];
  
  //   if (startRaw && endRaw) {
  //     const start = new Date(startRaw);
  //     const end = new Date(endRaw);
  //     setSelectedRange([start, end]);
  
  //     const filtered = timestamps
  //       .map((ts, i) => ({ time: new Date(ts), value: dataPoints[i] }))
  //       .filter(d => d.time >= start && d.time <= end)
  //       .map(d => d.value)
  //       .filter(v => !isNaN(v));
  
  //     if (filtered.length > 0) {
  //       const sorted = [...filtered].sort((a, b) => a - b);
  //       const min = Math.min(...filtered);
  //       const max = Math.max(...filtered);
  //       const median = sorted.length % 2 === 0
  //         ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
  //         : sorted[Math.floor(sorted.length / 2)];
  //       // console.log({min, max, median});
  //       setStats({ min, max, median: parseFloat(median) });
  //     } else {
  //       setStats({ min: null, max: null, median: null });
  //     }
  //   }
  // };

  const filtered = React.useMemo(() => {
    if(dataPoints.length === 0 || timestamps.length === 0) return {};
    console.log(timestamps.slice(0, 10));
    let obj = {};
    let final = {};
    IMAGERY_DATES.forEach(imageDate => obj[imageDate] = []);

    timestamps.forEach((stamp, index) => {
      if(obj[stamp.split('T')[0]]){
        obj[stamp.split('T')[0]].push(dataPoints[index]);
      }
    });

    Object.keys(obj).forEach(key => {
      const sorted = [...obj[key]].sort((a, b) => a - b);
      const min = Math.min(...obj[key]);
      const max = Math.max(...obj[key]);
      const median = sorted.length % 2 === 0
        ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
        : sorted[Math.floor(sorted.length / 2)];
      final[key] = {min, max, median};
    })

    return final;
  }, [dataPoints, timestamps]);
  

  return <div>
    <h1>Fort Peck Test Flow Bank Analysis</h1>
    <div style={{display: 'flex', gap: 8}}>
      {
        DATE_RANGES.map(range => {
          return <DefaultButton 
            checked={selectedRange && range.name === selectedRange.name} 
            text={range.name} 
            onClick={() => setSelectedRange(range)} />
        })
      }
      <DefaultButton text="Reset" onClick={() => setSelectedRange(null)} />
    </div>
    <div style={{height: 500, width: '100%', background: '#fff', alignItems: 'center', justifyContent: 'center'}}>
      {
        loading ? <Spinner /> : <Plot
          data={[
            {
              x: timestamps,
              y: dataPoints,
              type: 'scatter',
              mode: 'lines',
              name: 'Discharge (cfs)',
            },
          ]}
          // onRelayout={handleRelayout}
          layout={{
            title: "Missouri River Discharge at Wolf Point",
            xaxis: { title: 'Date', type: 'time', range: selectedRange ? selectedRange.range : null },
            yaxis: { title: 'Discharge (cfs)' },
            // updatemenus: [
            //   {
            //     type: 'buttons',
            //     direction: 'right',
            //     showactive: true,
            //     x: 0.1,
            //     y: 1.15,
            //     buttons: [
            //       {
            //         label: 'April 2024',
            //         method: 'relayout',
            //         args: [
            //           'xaxis.range',
            //           ['2024-04-01', '2024-04-30'],
            //         ],
            //       },
            //       {
            //         label: 'May 2024',
            //         method: 'relayout',
            //         args: [
            //           'xaxis.range',
            //           ['2024-05-01', '2024-05-31'],
            //         ],
            //       },
            //       {
            //         label: 'June 2024',
            //         method: 'relayout',
            //         args: [
            //           'xaxis.range',
            //           ['2024-06-01', '2024-06-30'],
            //         ],
            //       },
            //       {
            //         label: 'Reset Zoom',
            //         method: 'relayout',
            //         args: ['xaxis.autorange', true],
            //       },
            //     ],
            //   },
            // ],
          }}
          useResizeHandler
          style={{ width: '100%', height: '100%' }}
        />
      }

    </div>
    {selectedRange && (
      <div style={{ marginTop: '1rem' }}>
        <p>Stats for {selectedRange.range[0]} – {selectedRange.range[1]}:</p>
        {stats.min !== null ? (
          <ul>
            <li><strong>Max Flow:</strong> {stats.max} cfs</li>
            <li><strong>Median Flow:</strong> {stats.median} cfs</li>
            <li><strong>Min Flow:</strong> {stats.min} cfs</li>
          </ul>
        ) : (
          <p>No data in this range.</p>
        )}
      </div>
    )}
    
  </div>
}